/**
 * Utility functions for parsing dates without timezone shifts
 * 
 * Handles both:
 * - Simple date strings: "2025-10-18"
 * - ISO datetime strings: "2025-10-18T00:00:00Z" or "2025-10-18T00:00:00"
 * 
 * Returns a local Date object (no timezone shift)
 */

export function parseLocalDate(dateStr: string | Date): Date {
  // If already a Date object, return it
  if (dateStr instanceof Date) {
    return dateStr;
  }

  // Handle null/undefined
  if (!dateStr) {
    return new Date();
  }

  // Extract just the date part (YYYY-MM-DD) from any format
  // This works for both "2025-10-18" and "2025-10-18T00:00:00Z"
  const datePart = dateStr.split('T')[0];
  
  // Split into components
  const parts = datePart.split('-');
  
  if (parts.length !== 3) {
    console.warn('[parseLocalDate] Invalid date format:', dateStr, '- using current date as fallback');
    return new Date(); // Fallback to current date
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Validate numbers
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    console.warn('[parseLocalDate] Invalid date components:', dateStr, { year, month, day }, '- using current date as fallback');
    return new Date(); // Fallback to current date
  }

  // Create local date (month is 0-indexed in JavaScript)
  const localDate = new Date(year, month - 1, day);
  
  // Validate the resulting date
  if (isNaN(localDate.getTime())) {
    console.warn('[parseLocalDate] Invalid date created:', dateStr, '- using current date as fallback');
    return new Date();
  }
  
  return localDate;
}

/**
 * Format date for display on X-axis (e.g., "Oct 18")
 */
export function formatXAxisDate(dateStr: string | Date): string {
  const date = parseLocalDate(dateStr);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Format date for tooltips (e.g., "Monday, October 18, 2025")
 */
export function formatTooltipDate(dateStr: string | Date): string {
  const date = parseLocalDate(dateStr);
  return date.toLocaleDateString(undefined, { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
}

